import { useApolloClient } from '@apollo/client';
import { Theme } from '@busybusy/webapp-react-ui';
import { GET_PROJECT_ID_QUERY } from 'apollo/queries/project-queries';
import { FolderIcon } from 'assets/icons';
import classNames from 'classnames';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import { TimeEntryActionBarContextProvider } from 'components/domain/time-entry/TimeEntryActionBar/TimeEntryActionBarContext';
import DashboardActiveList from 'components/foundation/dashboard/dashboard-list/DashboardActiveList/DashboardActiveList';
import DashboardContentCard from 'components/foundation/dashboard/DashboardContentCard/DashboardContentCard';
import DashboardEnableCard from 'components/foundation/dashboard/DashboardEnableCard/DashboardEnableCard';
import ActivityDetailsDialog from 'containers/activity-reports/activity-report-details/ActivityDetailsDialog/ActivityDetailsDialog';
import { ActivityReportType } from 'containers/activity-reports/ActivityReportFilter/ActivityReportFilter';
import ChannelContextProvider from 'contexts/ChannelContext/ChannelContext';
import { useOpennable, useProjectManagementListNavigation } from 'hooks';
import useProjectDashboardNavigation from 'hooks/navigation/useProjectDashboardNavigation';
import { isEmpty, isNull } from 'lodash';
import { useEffect, useState } from 'react';
import IProject from 'types/Project';
import { colors } from 'utils/constants/colors';
import { t } from 'utils/localize';
import DashboardCardSpinner from '../DashboardCardSpinner/DashboardCardSpinner';
import DashboardErrorCard from '../DashboardErrorCard/DashboardErrorCard';
import useActiveCardReportNavigation from '../hooks/useActiveCardReportNavigation';
import useActivityReportDetailNavigation from '../hooks/useActivityReportDetailNavigation';
import useActiveProjectDashboard from './hooks/useActiveProjectsDashboard';

export interface IActiveProjectsDashboardCardProps {
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  onHide: () => void;
  className?: string | object;
  memberId?: string | null;
  costCodeId?: string | null;
  equipmentId?: string | null;
}

const ActiveProjectsDashboardCard = ({
  setting,
  theme,
  onHide,
  className,
  memberId,
  costCodeId,
  equipmentId,
}: IActiveProjectsDashboardCardProps) => {
  const projectManagementNavigation = useProjectManagementListNavigation();
  const projectDashboardNavigation = useProjectDashboardNavigation();
  const activityReportNavigation = useActiveCardReportNavigation(setting.options?.time, 'by-project');
  const { timeRangeType, navRange, getActivityReportFilter, getActivityReportFilterType } =
    useActivityReportDetailNavigation(setting.options?.time, memberId, costCodeId, equipmentId);
  const activityReportDetailsDialog = useOpennable();
  const client = useApolloClient();

  const { data, loading, error, title, hasAnyProject, execute } = useActiveProjectDashboard(
    setting,
    memberId,
    costCodeId,
    equipmentId
  );
  const [shouldShowCreateProjects, setShouldShowCreateProjects] = useState(false);

  useEffect(() => {
    getProject();
  }, [setting, memberId, costCodeId, equipmentId]);

  async function getProject() {
    const results = await client.query<{ projects: IProject[] }>({
      query: GET_PROJECT_ID_QUERY,
      variables: {
        first: 1,
      },
    });
    setShouldShowCreateProjects(isEmpty(results.data.projects));
  }

  function openProjectManagementList() {
    projectManagementNavigation();
  }

  function openProjectDashboard(projectId: string) {
    projectDashboardNavigation({ project_id: projectId });
  }

  function renderList() {
    return data && data?.length > 0 ? (
      <DashboardActiveList rowItems={data} onRowClick={openProjectDashboard} theme={theme} />
    ) : null;
  }

  function handleRightClick() {
    if (isNull(getActivityReportFilter())) {
      activityReportNavigation();
    } else {
      activityReportDetailsDialog.open();
    }
  }

  const classes = classNames('active-project-dashboard-card', className);

  return (
    <>
      {!shouldShowCreateProjects || loading ? (
        !error ? (
          <DashboardContentCard
            subheaderValue={title}
            tabColor={colors.primary}
            className={classes}
            theme={theme}
            rightLabel={t('View Report')}
            onRightLabelClick={handleRightClick}
          >
            {loading ? <DashboardCardSpinner /> : renderList()}
          </DashboardContentCard>
        ) : (
          <DashboardErrorCard theme={theme} onClick={execute} />
        )
      ) : (
        <DashboardEnableCard
          setting={setting}
          svg={FolderIcon}
          buttonLabel={t('Create Projects')}
          onClick={openProjectManagementList}
          theme={theme}
          onHide={onHide}
        />
      )}
      {!isNull(timeRangeType) && !isNull(navRange) && (
        <TimeEntryActionBarContextProvider>
          <ChannelContextProvider>
            <ActivityDetailsDialog
              isOpen={activityReportDetailsDialog.isOpen}
              onClose={activityReportDetailsDialog.close}
              timeRange={navRange}
              timeRangeType={timeRangeType}
              filterType={getActivityReportFilterType()}
              filterId={getActivityReportFilter()}
              initialReportType={ActivityReportType.BY_PROJECT}
            />
          </ChannelContextProvider>
        </TimeEntryActionBarContextProvider>
      )}
    </>
  );
};

export default ActiveProjectsDashboardCard;
